// Display-side API. Auth is a long-lived display token (not a user JWT).
// API_URL defaults to '' so the display app's nginx can same-origin-proxy
// /api/* to the backend, mirroring the SPA's pattern.

import { useDisplayStore } from './store';

export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export interface PairResponse {
  displayToken: string;
  display: DisplayPublic;
}

export interface DisplayPublic {
  id: string;
  name: string;
  channelType: string;
  orientation: string;
  status: string;
  venueName: string;
  orgName: string;
  displayConfig: Record<string, unknown> | null;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (init.auth !== false) {
    const token = useDisplayStore.getState().displayToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
  }
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      code?: string;
    };
    const message = body.message ?? `HTTP ${res.status}`;
    throw new ApiError(
      res.status,
      body.code ?? String(res.status),
      Array.isArray(message) ? message.join(', ') : message,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function deviceInfo() {
  return {
    userAgent: navigator.userAgent,
    resolutionW: window.screen.width,
    resolutionH: window.screen.height,
    orientation:
      window.screen.height > window.screen.width ? 'portrait' : 'landscape',
  };
}

export const api = {
  pair(pairingCode: string) {
    return request<PairResponse>('/api/displays/pair', {
      method: 'POST',
      auth: false,
      body: JSON.stringify({ pairingCode, deviceInfo: deviceInfo() }),
    });
  },
  heartbeat() {
    return request<DisplayPublic>('/api/displays/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ deviceInfo: deviceInfo() }),
    });
  },
};

export { ApiError };
